import { Card, Typography, Chip, Stack, Button, Collapse } from "@mui/material";
import { Box } from "@mui/system";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../assets/styles/tokens";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React from "react";
import NegativeBalanceMoreMetrics from "./NegativeBalanceMoreMetrics";

// ─────────────────────────────────────────────────────────────────────────────

type StatusColor = "success" | "error";

interface PerformanceItemCardProps {
  website_name: string;
  website_id: string;
  totalIncome: string;
  cpa: string;
  cpaPercentage: string;
  cpaColor: StatusColor;
  rs: string;
  rsPercentage: string;
  rsColor: StatusColor;
  reg: string;
  click_out: string;

  ftd: string;
  ftd_status: string;
  ftd_percentage: string;
  qftd: string;
  qftd_status: string;
  qftd_percentage: string;
  ftd_incent_percentage: string;
  qftd_incent_percentage: string;
  ftd_incent_status: string;
  qftd_incent_status: string;

  total_income_: string;
  total_income_Percentage: string;
  roi: string;
  net_rev: string;
  pv: string;
  epc: string;
  epc_cpa: string;
  cpa_pv: string;
  cpa_pv_qftd: string;
}

export function NegativeBalanceCard({
  website_name,
  website_id,
  totalIncome,
  ftd,
  ftd_status,
  qftd,

  total_income_,
  total_income_Percentage,
  roi,
  net_rev,
  pv,
  epc,
  epc_cpa,
  cpa_pv,
  cpa_pv_qftd,
}: PerformanceItemCardProps) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Card
        sx={{
          p: 1,
          borderRadius: spacing.Radius.lg.$value,
          boxShadow: "0px 1px 2px #0A0D120F , 0px 1px 3px #0A0D121A",
          border: "1px solid" + colors.GrayLightMode[200].$value,
          mb: spacing.Spacing.none.$value,
          mt: spacing.Spacing.xl.$value + "!important",
        }}
      >
        {/* Top Row */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="body1"
            sx={{
              color: colors.GrayLightMode[700].$value,
              fontSize: typography.Size.Text.xs.$value,
              fontWeight: typography.Font_weight.Medium.$value,
              mb: spacing.Spacing.md.$value,
            }}
          >
            {website_name} ({website_id})
          </Typography>
        </Stack>

        <Stack
          sx={{
            mb: spacing.Spacing.md.$value,
            justifyContent: "space-between",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: typography.Size.Text.sm.$value,
              color: colors.GrayLightMode[900].$value,
            }}
          >
            Calculated Total Income
          </Typography>
          <Chip
            label={totalIncome}
            size="medium"
            sx={{
              borderRadius: spacing.Radius.xs.$value,
              fontSize: typography.Size.Text.md.$value,
              bgcolor:
                ftd_status === "success"
                  ? colors.Success[50].$value
                  : ftd_status === "warning"
                    ? colors.Warning[50].$value
                    : colors.Error[50].$value,

              color:
                ftd_status === "success"
                  ? colors.Success[600].$value
                  : ftd_status === "warning"
                    ? colors.Warning[600].$value
                    : colors.Error[600].$value,

              fontWeight: typography.Font_weight.Semibold.$value,
            }}
          />
        </Stack>

        <Stack
          direction="row"
          justifyContent="space-between"
          mt={spacing.Spacing.xs.$value}
          mb={spacing.Spacing.md.$value}
        >
          <Box
            sx={{
              width: "50%",
              borderRight: "1px solid" + colors.GrayLightMode[200].$value,
              pr: spacing.Spacing.md.$value,
            }}
          >
            <Typography
              mb={spacing.Spacing.xs.$value}
              sx={{
                color: colors.GrayLightMode[500].$value,
                fontSize: typography.Size.Text.xs.$value,
              }}
            >
              Last Month End Balance
            </Typography>
            <Box>
              <Typography
                sx={{
                  color: colors.GrayLightMode[900].$value,
                  fontSize: typography.Size.Text.md.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  pr: spacing.Spacing.xs.$value,
                }}
              >
                {ftd}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: "50%",
              pl: spacing.Spacing.md.$value,
            }}
          >
            <Typography
              mb={spacing.Spacing.xs.$value}
              sx={{
                color: colors.GrayLightMode[500].$value,
                fontSize: typography.Size.Text.xs.$value,
              }}
            >
              Total Income
            </Typography>
            <Box>
              <Typography
                sx={{
                  color: colors.GrayLightMode[900].$value,
                  fontSize: typography.Size.Text.md.$value,
                  fontWeight: typography.Font_weight.Semibold.$value,
                  pr: spacing.Spacing.xs.$value,
                }}
              >
                {qftd}
              </Typography>
            </Box>
          </Box>
        </Stack>

        <Stack>
          <Button
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              padding: "0",
              fontSize: typography.Size.Text.xs.$value,
              lineHeight: typography.Line_height.text.xs.$value,
              color: colors.Brand[800].$value,
              fontWeight: typography.Font_weight.Regular.$value,
              textTransform: "capitalize",
              letterSpacing: "0px",
            }}
            data-tracker="654"
          >
            {open ? "Less More Metrics" : "Show More Metrics"}
            <ExpandMoreIcon
              sx={{
                color: colors.Brand[800].$value,
                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.2s",
              }}
            />
          </Button>

          {/* Expandable Info */}
          <Collapse in={open}>
            <NegativeBalanceMoreMetrics
              total_income_={total_income_}
              total_income_Percentage={total_income_Percentage}
              roi={roi}
              net_rev={net_rev}
              pv={pv}
              epc={epc}
              epc_cpa={epc_cpa}
              cpa_pv={cpa_pv}
              cpa_pv_qftd={cpa_pv_qftd}
            />
          </Collapse>
        </Stack>
      </Card>
    </>
  );
}
export default NegativeBalanceCard;
